import HybridCrypto from '@/utils/hybrid_crypto'
import { getDevMockProfile, getDevMockScenario } from '@/utils/devMock'

import type { User, ProjectConfig, Card, CardBalance, CardDetails, Tx, TxLast, CreateSessionResponse } from '@/types/shared'

export const useAuthStore = defineStore('auth_store', {
	state: () => ({
		user: null as User | null,
		is_auth_modal_visible: false,
		
		status: 'idle' as 'idle' | 'loading' | 'success' | 'error',
		auth_error_code: '' as '' | 'TG_DATA' | 'SESSION' | 'LOGIN' | 'LOGIN_NETWORK' | 'LOGIN_REJECTED' | `LOGIN_HTTP_${number}` | 'USER' | 'CARD_CONFIG',

		tg_init_data_hash: '',

		session_token: '',
		
		client_public_key: '',
		client_private_key: '',

		server_public_key: '',

		tron_wallet_address: '',

		project_config: null as ProjectConfig | null,

		blue_c_state: null as Card | null,
		blue_c_details: null as CardDetails | null,
		blue_c_balance: null as CardBalance | null,

		is_blue_c_balance_loading: false as boolean,

		txs_last: null as TxLast | null,

		is_debug_log: false as boolean,
	}),

	getters: {
		isAuthenticated(): boolean {
			return !!this.session_token
		},
		is_su(): boolean {
			return this.user?._id?.$oid == '676452292ae81320fe448499' || false
		}
	},

	actions: {

		store_tg_init_data_hash(hash: string) {
			if (this.is_debug_log) { console.warn('Storing Telegram init data hash:', hash) }
			this.tg_init_data_hash = hash
		},
		
		async generate_client_keys() {
			if (this.is_debug_log) { console.log('Generating client keys...')}

			const keypair = await HybridCrypto.generateKeyPair()

			if (this.is_debug_log) { console.log('Client public key:', keypair.publicKeyPem)}
			if (this.is_debug_log) { console.log('Client private key:', keypair.privateKeyPem)}

			this.client_public_key = keypair.publicKeyPem
			this.client_private_key = keypair.privateKeyPem
		},

		async ensure_session() {
			this.auth_error_code = ''
			if (getDevMockScenario()) {
				const profile = getDevMockProfile()
				this.session_token = 'dev-mock'
				this.user = profile.user
				this.project_config = profile.project_config
				this.blue_c_state = profile.blue_c_state
				this.tron_wallet_address = profile.tron_wallet_address
				this.txs_last = profile.txs_last
				this.status = 'success'
				return
			}
			this.user = null
			this.project_config = null
			const session_storage = useLocalStorage('session', null)
			if (session_storage.value) {
				const session = JSON.parse(session_storage.value || '{}')
				if (session) {
					console.log('Session exists:', session)
					this.session_token = session['session_token']
					this.client_public_key = session['client_public_key']
					this.client_private_key = session['client_private_key']
					this.server_public_key = session['server_public_key']
				}
				if (this.is_debug_log) { console.log('Session restored:', session) }
				await this.get_user()
				if (this.user && this.project_config?.blue_card) {
					this.status = 'success'
				} else {
					session_storage.value = null
					this.session_token = ''
					await this.create_session()
				}
				if (this.is_debug_log) { console.log('User restored:', this.user) }
			} else {
				console.log('Session is null')
				if (this.is_debug_log) { console.log('Session is null') }
				await this.create_session()
				if (this.is_debug_log) { console.log('Session created') }
			}
		},

		async restore_session() {
			const session_storage = useLocalStorage('session', null)
			if (session_storage.value) {
				const session = JSON.parse(session_storage.value || '{}')
				if (session) {
					this.session_token = session['session_token']
					this.client_public_key = session['client_public_key']
					this.client_private_key = session['client_private_key']
					this.server_public_key = session['server_public_key']
				}
				if (this.is_debug_log) { console.log('Session restored:', session) }
			}

			return session_storage
		},

		async decrypt_data_with_client_private_key_and_otk(encrypted: string, otk: string) {

			// const otk_decrypted = await HybridCrypto.decrypt(
			// 	this.client_private_key,
			// 	otk,
			// )

			// console.log('OTK decrypted:', otk_decrypted)

			// const sayfer_web = new SayferWeb(otk_decrypted)

			// console.log('SayferWeb instance created:', sayfer_web)
			// console.log('Encrypted data:', encrypted)

			// const decrypted = await sayfer_web.decrypt(encrypted)

			// console.log('Decrypted data:', decrypted)
			// return decrypted

			return await HybridCrypto.hybridDecrypt(
				this.client_private_key,
				encrypted,
				otk,
			)

		},

		async create_session() {
			console.log('Creating session...')
			if (!this.tg_init_data_hash) {
				this.auth_error_code = 'TG_DATA'
				this.status = 'error'
				return
			}
			this.auth_error_code = 'SESSION'

			await this.generate_client_keys()
			
			const api = useApi()
			const session_storage = useLocalStorage('session', null)

			const create_session_response = await api.post<CreateSessionResponse>('/api/hybrid/create_session', {
				client_public_key: this.client_public_key,
			})

			if (this.is_debug_log) { console.log('Session created:', create_session_response) }

			const session_create_otk_decrypted = await HybridCrypto.decrypt(
				this.client_private_key,
				create_session_response.otk,
			)

			 

			if (this.is_debug_log) {  console.log('OTK decrypted:', session_create_otk_decrypted) }

			const sayfer_web = new SayferWeb(session_create_otk_decrypted)

			const server_public_key_decrypted = await sayfer_web.decrypt(
				create_session_response.server_public_key,
			)

			if (this.is_debug_log) { console.log('Server public key decrypted:', server_public_key_decrypted) }

			this.server_public_key = server_public_key_decrypted
			this.session_token = create_session_response.session_token
			
			// Generate a random 32-byte array and convert it to base64
			const randomBytes = new Uint8Array(32)
			crypto.getRandomValues(randomBytes)
			const login_otk_decrypted = btoa(String.fromCharCode.apply(null, [...randomBytes]))

			if (this.is_debug_log) { console.log('Login OTK decrypted:', login_otk_decrypted) }

			const init_data_encrypted = await HybridCrypto.hybridEncrypt(
				server_public_key_decrypted,
				this.tg_init_data_hash,
				login_otk_decrypted,
			)

			if (this.is_debug_log) { console.log('Init data hash:', this.tg_init_data_hash) }

			if (this.is_debug_log) { console.log('Init data encrypted:', init_data_encrypted) }

			console.log('TMA login start...')
			this.auth_error_code = 'LOGIN'

			const tma_login_response = await api.post('/api/hybrid/tma_login', {
				session_token: this.session_token,
				init_data: init_data_encrypted.encryptedData,
				otk: init_data_encrypted.encryptedPassword,
			}).catch((error: unknown) => {
				const statusCode = (error as { statusCode?: number }).statusCode
				this.auth_error_code = typeof statusCode === 'number' ? `LOGIN_HTTP_${statusCode}` : 'LOGIN_NETWORK'
				throw error
			})

			console.log('TMA login response:', tma_login_response)

			if (tma_login_response.success && tma_login_response.user && tma_login_response.project_config?.blue_card) {
				
				this.user = tma_login_response.user
				this.session_token = create_session_response.session_token
				this.tron_wallet_address = tma_login_response.tron_wallet_address
				this.project_config = tma_login_response.project_config
				this.blue_c_state = tma_login_response.blue_c_state
				this.txs_last = tma_login_response.txs_last
				
				session_storage.value = JSON.stringify({
					session_token: this.session_token,
					client_public_key: this.client_public_key,
					client_private_key: this.client_private_key,
					server_public_key: this.server_public_key,
				})
				
				this.status = 'success'
				this.auth_error_code = ''
				
			} else {
				console.error('TMA login error:', tma_login_response)
				this.auth_error_code = !tma_login_response.success ? 'LOGIN_REJECTED' : !tma_login_response.user ? 'USER' : 'CARD_CONFIG'
				this.status = 'error'
			}
			
		},
		async get_user() {
			const api = useApi()

			try {
				const response = await api.post('/api/hybrid/get_user')

				if (response.success) {
					this.user = response.user
					this.tron_wallet_address = response.tron_wallet_address
					this.project_config = response.project_config
					this.blue_c_state = response.blue_c_state
					this.txs_last = response.txs_last
				}

				return response
			} catch (error) {
				console.error('Get user error:', error)
				// throw error
			}
		},

		async update_blue_c_balance() {
			const api = useApi()

			if (this.is_blue_c_balance_loading) {
				return
			}

			this.is_blue_c_balance_loading = true

			try {
				const response = await api.post('/api/hybrid/blue_c_balance')

				if (response.success) {
					this.blue_c_balance = response.item
				}

				return response
			} catch (error) {
				console.error('Update ', error)
				// throw error
			} finally {
				this.is_blue_c_balance_loading = false
			}
		},

		// Выпуск карты за счёт USDT-баланса: бэкенд создаёт заказ, списывает
		// цену выпуска и сразу переводит его в 'paid' — дальше KYC.
		// Провайдера (HiPay / asinx) выбирает бэкенд, TMA о нём не знает.
		async blue_c_issue_pay() {
			const api = useApi()

			const response = await api.post('/api/hybrid/blue_c_issue_pay')

			if (response.success) {
				this.user = response.user
				this.blue_c_state = response.blue_c_state
				this.project_config = response.project_config
				this.tron_wallet_address = response.tron_wallet_address
			}

			return response
		},

		async update_user_language(code: 'ru' | 'en') {
			const api = useApi()

			try {
				const response = await api.post('/api/hybrid/update_user_language', { code })

				if (response.success) {
					this.user = response.user
				}

				return response
			} catch (error) {
				console.error('Update user language error:', error)
				// throw error
				
			}
		}

	}
})
